// @ts-nocheck - Amplify Gen 2 type compatibility issues
/**
 * G11 Tariff Calculator (Single-Rate)
 *
 * @description Calculates costs for G11 flat-rate tariff
 * @version 2.1.0
 * @created 2025-01-26
 * @author eCompanion2 Energy Tariff Calculator
 *
 * Extracted from tariff-calculator.ts for better modularity
 */

import type { G11Pricing, MonthlyResult } from './types';
import {
  VAT_RATE,
  HANDLING_FEE,
  SUBSCRIPTION_FEE,
  TRANSITION_FEE,
  POWER_FEE,
  OZE_FEE,
  COGENERATION_FEE,
  isPowerFeeFrozen,
} from './constants';
import { roundToTwoDecimals } from './helpers';

/**
 * Calculate G11 (single-rate) tariff cost
 *
 * Formula:
 * - Energy cost = kWh × (energyPrice + qualityCharge)
 * - Distribution cost = fixedFee + (kWh × distributionPerKwh)
 * - Variable fees = kWh × (ozeFee + cogenerationFee)
 * - Fixed fees = handlingFee + subscriptionFee + transitionFee + powerFee
 * - Total (gross) = (Energy + Distribution + Variable + Fixed) × 1.23
 *
 * @param monthlyConsumption - kWh consumed in month
 * @param pricing - G11 pricing data from EnergyProvider
 * @param month - Month (0-indexed, 0=Jan) - for power fee frozen period check
 * @param year - Year - for power fee frozen period check
 * @returns Monthly calculation result
 *
 * @example
 * const result = calculateG11(300, TAURON_2025_PRICING.g11, 0, 2025);
 * // Returns complete breakdown including all fees
 */
export function calculateG11(
  monthlyConsumption: number,
  pricing: G11Pricing,
  month: number = 6,  // Default to July (outside frozen period)
  year: number = 2025
): MonthlyResult {
  // Input validation - HIGH-1 Security: Validate for NaN, Infinity, and negative values
  if (!Number.isFinite(monthlyConsumption) || monthlyConsumption < 0) {
    throw new Error('Monthly consumption must be a finite non-negative number');
  }
  if (!pricing) {
    throw new Error('Pricing data is required');
  }

  // Calculate energy production cost
  const energyPriceTotal = pricing.energyPricePerKwh + (pricing.qualityCharge || 0);
  const productionCost = monthlyConsumption * energyPriceTotal;

  // Calculate distribution cost
  const distributionVariable = monthlyConsumption * pricing.distributionPerKwh;
  const distributionCost = pricing.fixedMonthlyFee + distributionVariable;

  // Get fees - use defaults if not provided
  const handlingFee = pricing.handlingFee ?? HANDLING_FEE.REGULATED;
  const subscriptionFee = pricing.subscriptionFee ?? SUBSCRIPTION_FEE;
  const transitionFee = pricing.transitionFee ?? TRANSITION_FEE.TIER_3;

  // Power fee is 0 for Jan-June 2025
  const basePowerFee = pricing.powerFee ?? POWER_FEE.TIER_3;
  const powerFee = isPowerFeeFrozen(month, year) ? 0 : basePowerFee;

  // Variable fees (per kWh)
  const ozeFeeAmount = monthlyConsumption * (pricing.ozeFee ?? OZE_FEE);
  const cogenerationFeeAmount = monthlyConsumption * (pricing.cogenerationFee ?? COGENERATION_FEE);

  // Calculate totals
  const subtotal = productionCost + distributionCost +
    handlingFee + subscriptionFee + transitionFee + powerFee +
    ozeFeeAmount + cogenerationFeeAmount;
  const totalCost = subtotal * (1 + VAT_RATE);

  return {
    totalKwh: monthlyConsumption,
    productionCost: roundToTwoDecimals(productionCost),
    distributionCost: roundToTwoDecimals(distributionCost),
    handlingFee: roundToTwoDecimals(handlingFee),
    subscriptionFee: roundToTwoDecimals(subscriptionFee),
    transitionFee: roundToTwoDecimals(transitionFee),
    powerFee: roundToTwoDecimals(powerFee),
    ozeFee: roundToTwoDecimals(ozeFeeAmount),
    cogenerationFee: roundToTwoDecimals(cogenerationFeeAmount),
    subtotal: roundToTwoDecimals(subtotal),
    totalCost: roundToTwoDecimals(totalCost)
  };
}
